import { useState } from 'react'
import { toast } from 'sonner'
import type { PaymentMethod } from '@naya/shared'
import { Modal } from './Modal.js'
import { AdminApiError } from '../adminApi.js'
import { useMarkInvoicePaid, useVoidInvoice } from '../hooks/useInvoices.js'
import type { InvoiceRow } from '../types.js'

interface InvoiceActionsProps {
  invoice: InvoiceRow
}

/**
 * The status-driven action buttons + modals for a single invoice.
 *   unpaid / overdue → Mark paid, Void
 *   paid / void      → no actions (terminal for the 3a model)
 * Mark-paid requires a payment method + reference; void requires a reason.
 */
export function InvoiceActions({ invoice }: InvoiceActionsProps) {
  const [modal, setModal] = useState<'paid' | 'void' | null>(null)
  const markPaid = useMarkInvoicePaid()
  const voidInvoice = useVoidInvoice()

  // Mark-paid form state.
  const [method, setMethod] = useState<PaymentMethod>('gcash')
  const [reference, setReference] = useState('')
  const [paidAt, setPaidAt] = useState('')

  // Void form state.
  const [reason, setReason] = useState('')

  const actionable = invoice.status === 'unpaid' || invoice.status === 'overdue'

  function submitPaid() {
    markPaid.mutate(
      {
        invoiceId: invoice.id,
        payment_method: method,
        ...(reference.trim() ? { payment_reference: reference.trim() } : {}),
        ...(paidAt ? { paid_at: new Date(paidAt).toISOString() } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Invoice marked paid. Next period invoice generated.')
          setModal(null)
          setReference('')
          setPaidAt('')
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not mark the invoice paid.',
          ),
      },
    )
  }

  function submitVoid() {
    if (reason.trim().length === 0) {
      toast.error('A void reason is required.')
      return
    }
    voidInvoice.mutate(
      { invoiceId: invoice.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success('Invoice voided.')
          setModal(null)
          setReason('')
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not void the invoice.',
          ),
      },
    )
  }

  if (!actionable) return null

  return (
    <>
      <div className="admin-row-actions">
        <button
          type="button"
          className="admin-btn admin-btn-sm admin-btn-primary"
          onClick={() => setModal('paid')}
        >
          Mark paid
        </button>
        <button
          type="button"
          className="admin-btn admin-btn-sm admin-btn-danger"
          onClick={() => setModal('void')}
        >
          Void
        </button>
      </div>

      {modal === 'paid' && (
        <Modal title="Mark invoice paid" onClose={() => setModal(null)}>
          <p className="admin-audit-meta" style={{ marginTop: 0 }}>
            {invoice.invoice_number} · marking paid generates the next
            period's invoice automatically.
          </p>

          <div className="admin-field">
            <label className="admin-label">Payment method</label>
            <div className="admin-radio-group">
              <label className="admin-radio-opt">
                <input
                  type="radio"
                  name="payment_method"
                  checked={method === 'gcash'}
                  onChange={() => setMethod('gcash')}
                />
                GCash
              </label>
              <label className="admin-radio-opt">
                <input
                  type="radio"
                  name="payment_method"
                  checked={method === 'bank'}
                  onChange={() => setMethod('bank')}
                />
                Bank transfer
              </label>
            </div>
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="pay-ref">
              Payment reference
            </label>
            <input
              id="pay-ref"
              className="admin-input"
              placeholder="e.g. GCash ref no."
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
            <p className="admin-hint">Optional but recommended.</p>
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="pay-date">
              Paid date
            </label>
            <input
              id="pay-date"
              type="date"
              className="admin-input"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
            />
            <p className="admin-hint">Defaults to today if left blank.</p>
          </div>

          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={submitPaid}
              disabled={markPaid.isPending}
            >
              {markPaid.isPending ? 'Saving…' : 'Mark paid'}
            </button>
            <button
              type="button"
              className="admin-btn"
              onClick={() => setModal(null)}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {modal === 'void' && (
        <Modal title="Void invoice" onClose={() => setModal(null)}>
          <p className="admin-audit-meta" style={{ marginTop: 0 }}>
            {invoice.invoice_number} · voiding is permanent and cannot be
            undone.
          </p>
          <div className="admin-field">
            <label className="admin-label" htmlFor="void-reason">
              Reason
            </label>
            <textarea
              id="void-reason"
              className="admin-textarea"
              placeholder="Why is this invoice being voided?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-btn admin-btn-danger"
              onClick={submitVoid}
              disabled={voidInvoice.isPending}
            >
              {voidInvoice.isPending ? 'Voiding…' : 'Void invoice'}
            </button>
            <button
              type="button"
              className="admin-btn"
              onClick={() => setModal(null)}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </>
  )
}
